import DjangoMessages from 'components/DjangoMessages';
import Checkbox from 'components/Form/Checkbox';
import Checkboxes, {CheckboxOption} from 'components/Form/Checkboxes';
import Dropdown from 'components/Form/Dropdown';
import Field from 'components/Form/Field';
import FormActions from 'components/Form/FormActions';
import FormResult from 'components/Form/FormResult';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import TextArea from 'components/Form/TextArea';
import SideBySide from 'components/Layout/SideBySide';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TooltipWrapper from 'components/Tooltip';
import Value from 'components/Value';
import {useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {Option} from 'types';
import {setUrl} from 'utils/request';
import {onApiErrorAxios} from 'utils/request-axios/utils';

import {BackendData} from '../../types';
import {sendingSettingsLinks} from '../../utils';
import useSendingSettings from '../services';

const SendingMethods = () => {
    const {data} = useBackend<BackendData>();

    const options: CheckboxOption[] = [
        {label: t`Send via Client Inbox`, name: 'sendToClientinboxDefault'},
        {label: t`Secure Send`, name: 'sendBySecureSendDefault'},
        {label: t`Send by Post`, name: 'sendByPostDefault'},
    ];
    if (data.isUsingPeppolSend) {
        options.push({label: t`Peppol Send`, name: 'sendByPeppolsendDefault'});
    }

    const link = (
        <Link
            href={sendingSettingsLinks.howToSendDocuments}
            isOpenInNewTab={true}
            key="help-center"
        >
            {t`Help Center`}
        </Link>
    );

    return (
        <>
            <Checkboxes label={t`Sending Methods`} options={options} />
            <Field label="" type="value">
                <Value
                    value={{
                        type: 'custom',
                        value: (
                            <div className="text-light-secondary">{jt`Send by Post requires the purchase of MailPoints. See the ${link} (Japanese) for more information on our Sending Methods.`}</div>
                        ),
                    }}
                />
            </Field>
        </>
    );
};

const ClientInboxUserEditing = () => {
    const {flags} = useBackend<BackendData>();

    if (!flags.portal_allow_clientinbox_users_to_edit_users) return null;

    return (
        <Field label={t`Client Inbox User Editing`} type="checkbox">
            <Checkbox
                label={t`Allow Client to Edit Client Inbox Users`}
                name="defaultAllowClientinboxUsersToEditUsers"
            />
        </Field>
    );
};

const PasswordProtection = () => {
    return (
        <Field label={t`Password Protection`} type="checkbox">
            <Checkbox
                label={t`Send Separate Password Email`}
                name="pwProtectPickupPages"
            />
        </Field>
    );
};

const MakeleapsPayments = () => {
    const {data} = useBackend<BackendData>();
    return (
        <Field label={t`MakeLeaps Payments`} type="checkbox">
            <TooltipWrapper message={data.ccPaymentsEnabled.failureReason}>
                <Checkbox
                    disabled={!data.ccPaymentsEnabled.allowed}
                    label={t`Enable Credit Card Payments`}
                    name="enableCcPayments"
                />
            </TooltipWrapper>
        </Field>
    );
};

const ContactsForSecureSending = () => {
    const options: Option[] = [
        {
            label: t`Document's Contacts`,
            value: 'on',
        },
        {
            label: t`Client's Contacts`,
            value: 'off',
        },
    ];
    return (
        <Dropdown
            label={t`Contacts for Secure Sending`}
            name="useDocumentContact"
            options={options}
            type="select"
        />
    );
};

const DefaultSecureSendSubject = () => {
    const {data} = useBackend<BackendData>();
    return (
        <>
            <InputText
                helpText={t`Will be used as the default when adding new clients`}
                label={t`Default Secure Send Subject`}
                name="securesendSubject"
            />
            <TextArea
                helpText={t`Will be used as the default when adding new clients`}
                label={t`Default Secure Send Message`}
                name="securesendMessage"
                resize="auto"
                rows={4}
            />

            {data.securesendMessageSuffix && (
                <Field
                    helpText={[
                        t`The above text will be appended to the message.`,
                        t`The recipient does not have to sign up to MakeLeaps to download the PDF.`,
                    ].join('\n')}
                    label=""
                    type="value"
                >
                    <pre className="rounded border border-grey-300 bg-grey-100 p-2 text-sm">
                        {data.securesendMessageSuffix}
                    </pre>
                </Field>
            )}
        </>
    );
};

export type FormData = {
    defaultAllowClientinboxUsersToEditUsers: boolean;
    enableCcPayments: boolean;
    pwProtectPickupPages: boolean;
    securesendMessage: null | string;
    securesendSubject: null | string;
    sendByPeppolsendDefault: boolean;
    sendByPostDefault: boolean;
    sendBySecureSendDefault: boolean;
    sendToClientinboxDefault: boolean;
    useDocumentContact: string;
};

const useInitialValue = (): FormData => {
    const {data} = useBackend<BackendData>();
    const [result] = useState<FormData>(() => {
        return {
            defaultAllowClientinboxUsersToEditUsers:
                data.defaultAllowClientinboxUsersToEditUsers,
            enableCcPayments: data.enableCcPayments,
            pwProtectPickupPages: data.pwProtectPickupPages,
            securesendMessage: data.securesendMessage,
            securesendSubject: data.securesendSubject,
            sendByPeppolsendDefault: data.sendByPeppolsendDefault,
            sendByPostDefault: data.sendByPostDefault,
            sendBySecureSendDefault: data.sendBySecureSendDefault,
            sendToClientinboxDefault: data.sendToClientinboxDefault,
            useDocumentContact: data.useDocumentContact ? 'on' : 'off',
        };
    });
    return result;
};

type FormDataProcessed = {
    useDocumentContact: boolean;
} & Omit<FormData, 'useDocumentContact'>;

const processFormData = (data: FormData): FormDataProcessed => ({
    ...data,
    useDocumentContact: data.useDocumentContact === 'on' ? true : false,
});

const Page = () => {
    const initialData = useInitialValue();
    const methods = useForm<FormData>({
        defaultValues: initialData,
        shouldUnregister: true,
    });
    const api = useSendingSettings();

    const clientListLink = (
        <a
            href={sendingSettingsLinks.clientListView}
            key="client-list"
        >{t`client list`}</a>
    );

    const disableSubmit = methods.formState.isSubmitting;

    const handleSubmit = async (formData: FormData) => {
        await api
            .mutateAsync(processFormData(formData))
            .then(() => {
                setUrl(sendingSettingsLinks.sendingSettingsView);
            })
            .catch(onApiErrorAxios(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <DjangoMessages className="mb-4" />
                <NavigationTabs.Partner selection="sending_settings" />
                <div>
                    <SectionHeader
                        subtitle={jt`These settings are used as the default values when adding new clients. You can see or edit these settings for existing clients in the ${clientListLink}.`}
                        title={t`Document Sending`}
                    />
                    <SideBySide
                        className="mt-4"
                        classNameLabel="w-[14rem] max-w-[14rem]"
                    >
                        <SendingMethods />
                        <ClientInboxUserEditing />
                        <PasswordProtection />
                        <MakeleapsPayments />
                        <ContactsForSecureSending />
                        <DefaultSecureSendSubject />
                        <FormResult />
                        <FormActions align="left">
                            <SubmitButton
                                disabled={disableSubmit}
                            >{t`Save`}</SubmitButton>
                            <LinkButton
                                href={sendingSettingsLinks.sendingSettingsView}
                            >{t`Cancel`}</LinkButton>
                        </FormActions>
                    </SideBySide>
                </div>
            </form>
        </FormProvider>
    );
};
export default Page;
